import React from 'react';
import {
  Handshake,
  Building,
  Lightbulb,
  GraduationCap,
  Mail,
  Calendar,
  Download,
  Users,
  Cpu,
  Zap,
} from 'lucide-react';
import { useGetCollaborationContent } from '../services/collaboration';
import Loading from '../shared/components/Loading';
import Error from '../shared/components/Error';

const CollaborationPage: React.FC = () => {
  const { collaborationData, isLoading, error } = useGetCollaborationContent();

  // Helper function to parse description with line breaks and bullet points
  const parseDescription = (description: string | null | undefined) => {
    if (!description) return [];

    return description
      .replace(/\\r\\n/g, '\n') // Replace literal \r\n with actual line breaks
      .replace(/\r\n/g, '\n') // Replace actual \r\n with \n
      .split('\n')
      .filter((line) => line.trim())
      .map((line, index) => (
        <p key={index} className="mb-2 last:mb-0">
          {line.trim()}
        </p>
      ));
  };

  // Helper function to parse bullet point descriptions
  const parseBulletDescription = (description: string | null | undefined) => {
    if (!description) return [];

    // Handle different line break formats and split by bullet points
    return description
      .replace(/\\r\\n/g, '\n') // Replace literal \r\n with actual line breaks
      .replace(/\r\n/g, '\n') // Replace actual \r\n with \n
      .split(/\n|•/) // Split by line breaks or bullet points
      .filter((line) => line.trim()) // Remove empty lines
      .map((line, index) => {
        const cleanedLine = line.trim().replace(/^•\s*/, ''); // Remove any remaining bullet points
        return cleanedLine ? <li key={index}>• {cleanedLine}</li> : null;
      })
      .filter(Boolean); // Remove null entries
  };

  if (isLoading) {
    return <Loading message="Loading collaboration content..." />;
  }

  if (error || !collaborationData) {
    return <Error message={error || 'Failed to load collaboration content'} />;
  }

  const {
    collaboration_title,
    collaboration_title_first,
    collaboration_title_second,
    collaboration_title_third,
    collaboration_title_four,
    collaboration_title_five,
  } = collaborationData;

  return (
    <div className="py-6 sm:py-12">
      <div className="w-full px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg p-4 sm:p-6 lg:p-8">
          {/* Header */}
          <div className="mb-6 sm:mb-8">
            <h1 className="text-2xl sm:text-3xl font-bold mb-3 sm:mb-4 text-primary-500">
              {collaboration_title[0]?.title || 'Collaboration'}
            </h1>
            <div className="w-12 sm:w-16 h-0.5 rounded bg-primary-500"></div>
          </div>

          {/* Why Collaborate Section */}
          {collaboration_title_first[0] && (
            <div className="mb-6 sm:mb-8">
              <div className="flex items-start mb-4">
                <Handshake className="w-5 h-5 sm:w-6 sm:h-6 mr-3 mt-1 flex-shrink-0 text-primary-500" />
                <h2 className="text-lg sm:text-2xl font-semibold text-primary-500">
                  {collaboration_title_first[0].title}
                </h2>
              </div>
              <div className="text-base sm:text-lg leading-relaxed text-secondary-700">
                {parseDescription(collaboration_title_first[0].description)}
              </div>
            </div>
          )}

          {/* Opportunities for Collaboration */}
          {collaboration_title_second[0] && (
            <div className="mb-6 sm:mb-8">
              <div className="flex items-start mb-4 sm:mb-6">
                <Users className="w-5 h-5 sm:w-6 sm:h-6 mr-3 mt-1 flex-shrink-0 text-primary-500" />
                <h2 className="text-lg sm:text-2xl font-semibold text-primary-500">
                  {collaboration_title_second[0].title}
                </h2>
              </div>

              {collaboration_title_second[0].collaboration_sub_title_first.map(
                (subTitle, index) => {
                  const icons = [Building, Lightbulb, GraduationCap];
                  const Icon = icons[index] || Building;

                  return (
                    <div key={subTitle.id} className="mb-4 sm:mb-6">
                      <div className="flex items-start mb-3">
                        <Icon className="w-4 h-4 sm:w-5 sm:h-5 mr-2 mt-0.5 flex-shrink-0 text-primary-500" />
                        <h3 className="text-base sm:text-xl font-semibold text-primary-500">
                          {subTitle.title}
                        </h3>
                      </div>
                      <ul className="ml-6 sm:ml-7 space-y-1 sm:space-y-2 text-sm sm:text-base text-secondary-700">
                        {parseBulletDescription(subTitle.description)}
                      </ul>
                    </div>
                  );
                }
              )}
            </div>
          )}

          {/* Technical Expertise */}
          {collaboration_title_third[0] && (
            <div className="mb-6 sm:mb-8">
              <div className="flex items-start mb-4">
                <Cpu className="w-5 h-5 sm:w-6 sm:h-6 mr-3 mt-1 flex-shrink-0 text-primary-500" />
                <h2 className="text-lg sm:text-2xl font-semibold text-primary-500">
                  {collaboration_title_third[0].title}
                </h2>
              </div>
              <p className="text-base sm:text-lg mb-4 text-secondary-700">
                {collaboration_title_third[0].description}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4 sm:mb-6">
                {collaboration_title_third[0].collaboration_third_sub_title_first.map(
                  (subTitle) => (
                    <div
                      key={subTitle.id}
                      className="bg-gray-100 p-3 sm:p-4 rounded-lg"
                    >
                      <h4 className="font-semibold mb-2 text-sm sm:text-base text-primary-500">
                        {subTitle.title}
                      </h4>
                      <ul className="space-y-1 text-xs sm:text-sm text-secondary-700">
                        {parseBulletDescription(subTitle.description)}
                      </ul>
                    </div>
                  )
                )}
              </div>
            </div>
          )}

          {/* How to Collaborate */}
          {collaboration_title_four[0] && (
            <div className="mb-6 sm:mb-8">
              <div className="flex items-start mb-4">
                <Zap className="w-5 h-5 sm:w-6 sm:h-6 mr-3 mt-1 flex-shrink-0 text-primary-500" />
                <h2 className="text-lg sm:text-2xl font-semibold text-primary-500">
                  {collaboration_title_four[0].title}
                </h2>
              </div>
              <p className="text-base sm:text-lg mb-4 text-secondary-700">
                {collaboration_title_four[0].description}
              </p>
              <p className="text-base sm:text-lg mb-4 text-secondary-700">
                You can:
              </p>
              <div className="space-y-3 sm:space-y-4">
                <div className="flex items-start">
                  <Mail className="w-4 h-4 sm:w-5 sm:h-5 mr-3 mt-0.5 flex-shrink-0 text-primary-500" />
                  <span className="text-sm sm:text-base text-secondary-700">
                    Email us at:{' '}
                    <a
                      href="mailto:contact@vlsiclub.org"
                      className="text-primary-500 underline hover:opacity-80"
                    >
                      contact@vlsiclub.org
                    </a>
                  </span>
                </div>
                <div className="flex items-start">
                  <Calendar className="w-4 h-4 sm:w-5 sm:h-5 mr-3 mt-0.5 flex-shrink-0 text-primary-500" />
                  <span className="text-sm sm:text-base text-secondary-700">
                    Schedule a virtual meeting with our core team
                  </span>
                </div>
                <div className="flex items-start">
                  <Download className="w-4 h-4 sm:w-5 sm:h-5 mr-3 mt-0.5 flex-shrink-0 text-primary-500" />
                  <span className="text-sm sm:text-base text-secondary-700">
                    Download our club profile (PDF) for more details
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Contact Companies for Collaboration */}
          {collaboration_title_five[0] && (
            <div className="mb-6 sm:mb-8">
              <h2 className="text-lg sm:text-2xl font-semibold mb-4 sm:mb-6 text-primary-500">
                {collaboration_title_five[0].title}
              </h2>
              <p className="text-base sm:text-lg mb-6 text-secondary-700">
                {collaboration_title_five[0].description}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
                {/* VeriFast Technologies */}
                <div className="bg-secondary-50 border border-primary-500 rounded-lg p-6 sm:p-8 text-center">
                  <div className="w-20 h-20 sm:w-24 sm:h-24 mx-auto mb-4">
                    <img
                      src="/verifast.png"
                      alt="VeriFast Technologies Logo"
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <h3 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4 text-center text-primary-500">
                    VeriFast Technologies Inc.
                  </h3>
                  <p className="text-sm sm:text-base text-secondary-700 mb-4">
                    Founded in 2012, VeriFast Technologies was born out of a
                    passion for excellence and a commitment to providing
                    cutting-edge ASIC, SoC & FPGA Verification. Over the years,
                    VeriFast has grown into a leading provider of verification
                    solutions, trusted by some of the most influential companies
                    in the industry.
                  </p>
                  <div className="text-center">
                    <a
                      href="https://www.linkedin.com/company/verifast-technologies/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm sm:text-base text-primary-500 underline hover:opacity-80 cursor-pointer"
                    >
                      LinkedIn Profile
                    </a>
                  </div>
                </div>

                {/* ASICSoft Inc */}
                <div className="bg-secondary-50 border border-primary-500 rounded-lg p-6 sm:p-8 text-center">
                  <div className="w-20 h-20 sm:w-24 sm:h-24 mx-auto mb-4">
                    <img
                      src="/asicsoft.jpg"
                      alt="ASICSoft Inc Logo"
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <h3 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4 text-center text-primary-500">
                    ASICSoft Inc
                  </h3>
                  <p className="text-sm sm:text-base text-secondary-700 mb-4">
                    ASICSoft is the leader in staffing and consulting for
                    Engineering and IT projects. Launched in 2006 ASICSoft has
                    provided high quality talent and consulting services to
                    leading companies throughout the United States. Our clients
                    range from giants to start-ups in semiconductor, networking,
                    multimedia, consumer electronics, defense/aerospace,
                    medical, cloud, mobile, and beyond.
                  </p>
                  <div className="text-center">
                    <a
                      href="https://www.linkedin.com/company/asicsoft/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm sm:text-base text-primary-500 underline hover:opacity-80 cursor-pointer"
                    >
                      LinkedIn Profile
                    </a>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CollaborationPage;
